import { useSnackbarMessage } from '@/models/global/snackbarMessage';
import { i18nResponse } from '@/utils/locale';
import * as Sentry from '@sentry/vue';

export const parseResponse = (response: any) => {
  if (response.status === 200) {
    const error = undefined;
    const data = response.data;
    const headers = response.headers;
    return { error, data, headers };
  }
  const error = new Error(response.statusText);
  const data = {};
  return { error, data };
};

// Sentry 送信時に原因特定へつながる最小限のリクエスト情報を集める。
// url にはクエリ (allocation_pattern_label など) が含まれ、どのリクエストが
// どんな値で失敗したかを追える。トークンを含む headers やリクエストボディは
// 機密漏洩を避けるため意図的に含めない。
const buildErrorExtra = (_error: any) => ({
  stack: _error.stack,
  url: _error.config?.url,
  method: _error.config?.method,
  status: _error.response?.status,
  responseData: _error.response?.data,
});

export const parseError = (
  _error: any,
  setFormErrors: (errors: { id: string; message: string }[]) => void
) => {
  const { setSnackbarMessage } = useSnackbarMessage();

  // ユーザーによる中断は通知しない
  if (_error?.code === 'ERR_CANCELED') {
    return { error: { status: 499, data: {} }, data: {}, headers: {} };
  }

  if (_error.code === 'ERR_NETWORK') {
    setSnackbarMessage({
      message: i18nResponse('system.network_error'),
      type: 'error',
    });

    const rawUrl: string | undefined = _error?.config?.url;
    let path = rawUrl;
    // 組織は URL クエリか、axios interceptor が付与する params のどちらかに入る
    let organization: string | undefined = _error?.config?.params?.organization;
    try {
      if (rawUrl) {
        const url = new URL(rawUrl);
        path = url.pathname;
        // params から取得できない場合のフォールバックとして、URL クエリにあれば取得
        // 相対PATH等で new URLが失敗する可能性があるのでtryの中で実装する必要がある
        organization ??= url.searchParams.get('organization') ?? undefined;
      }
    } catch {
      // 相対 URL 等でパースに失敗した場合は元の値を使う
    }
    const method = _error?.config?.method?.toUpperCase();

    // 呼び出し元ごとに issue が分散しないよう、ネットワークエラーは 1 つの issue にまとめる
    Sentry.captureException(new Error(_error.message ?? 'ERR_NETWORK'), {
      fingerprint: ['network-error'],
      // タグは全イベント共通の名前空間のため、画面のパスと区別できるよう network.path とする
      // 組織・API で絞り込めるよう、contexts に加えてタグにも付与する
      tags: { 'network.path': path, organization },
      contexts: {
        network: { path, method, organization },
      },
      extra: buildErrorExtra(_error),
    });
    return { error: { status: 500, data: {} }, data: {}, headers: {} };
  }

  const error = {
    status: _error.response?.status,
    data: _error.response?.data?.message,
  };

  const data = _error.response?.data || {};
  const headers = _error.response?.headers || {};

  if (error.status === 403) {
    if (_error.response?.data?.error === 'user_inactive') {
      window.dispatchEvent(new CustomEvent('user-inactive'));
      return { error: { status: 403, data: {} }, data: {}, headers: {} };
    }
    window.dispatchEvent(new CustomEvent('forbidden'));
    return { error: { status: 403, data: {} }, data: {}, headers: {} };
  }

  // snackbarやフォームにエラーを表示する
  let errorMessage = '';
  if (error.status >= 500) {
    // エラー原因にかかわらず時間を置いた再試行を促すため、network_error を使用する。
    errorMessage = i18nResponse('system.network_error');
  } else {
    // errors.dataがオブジェクトの場合
    if (typeof error.data === 'object') {
      const errors = [];
      const bases = [];
      for (const key in error.data) {
        if (key === 'base') {
          bases.push(error.data[key]);
        } else {
          errors.push({ id: key, message: error.data[key] });
        }
      }
      if (errors.length > 0) {
        setFormErrors(errors);
      }
      if (bases.length > 0) {
        errorMessage = bases.join('');
      }
    } else if (typeof error.data === 'string') {
      errorMessage = error.data;
    } else {
      errorMessage = i18nResponse('system.render500');
    }
  }

  if (errorMessage !== '') {
    setSnackbarMessage({ message: errorMessage, type: 'error' });
  }
  // sentry
  if (![401, 422].includes(error.status))
    Sentry.captureException(
      new Error(_error.message ?? JSON.stringify(_error)),
      { extra: buildErrorExtra(_error) }
    );
  return { error, data, headers };
};

// fetch関数のレスポンスをパースする関数
const parseFetchResponseBody = async (response: Response) => {
  const contentType = response.headers.get('content-type') || '';
  const rawBody = await response.text();

  if (!rawBody) {
    return {};
  }

  if (!contentType.includes('application/json')) {
    return { message: rawBody };
  }

  try {
    return JSON.parse(rawBody);
  } catch {
    return { message: rawBody };
  }
};

const parseFetchHeaders = (headers: Headers) => {
  const parsedHeaders: Record<string, string> = {};
  headers.forEach((value, key) => {
    parsedHeaders[key] = value;
  });
  return parsedHeaders;
};

export type FetchSuccessResult = {
  error: undefined;
  data: Response;
  headers: Record<string, string>;
};
export type FetchFailureResult = {
  error: { status?: number; data?: any } | Error;
  data: any;
  headers?: any;
};
export type ParseFetchResult = FetchSuccessResult | FetchFailureResult;

export const parseFetchResponse = async (
  response: Response,
  setFormErrors: (errors: { id: string; message: string }[]) => void
): Promise<ParseFetchResult> => {
  if (response.ok) {
    return {
      error: undefined,
      data: response,
      headers: parseFetchHeaders(response.headers),
    };
  }

  const data = await parseFetchResponseBody(response);

  return parseError(
    {
      response: {
        status: response.status,
        data,
        headers: parseFetchHeaders(response.headers),
      },
    },
    setFormErrors
  );
};

type FailedRequest = { url: string; method: string; organization?: string };

export const parseFetchError = (
  error: unknown,
  setFormErrors: (errors: { id: string; message: string }[]) => void,
  request?: FailedRequest
): FetchFailureResult => {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return parseError({ code: 'ERR_CANCELED' }, setFormErrors);
  }

  return parseError(
    {
      message: error instanceof Error ? error.message : undefined,
      stack: error instanceof Error ? error.stack : undefined,
      code:
        error && typeof error === 'object' && 'code' in error
          ? (error as { code?: string }).code
          : 'ERR_NETWORK',
      // fetch のエラーには axios のような config が無いため、parseError が
      // path / method / 組織を読めるよう axios の config と同じ形にして渡す
      config: request && {
        url: request.url,
        method: request.method,
        params: { organization: request.organization },
      },
    },
    setFormErrors
  );
};
